import { useEffect, useMemo, useState } from 'react';
import type { Client, ClientInput } from '@sirajoptic/shared';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { InputField, TextareaField } from '@/components/ui/field';
import { useToast } from '@/components/ui/toast';
import { useCreerClient } from '../api/clients.api';

const VIDE: ClientInput = { nom: '', tel: null, ice: null, note: null };

/**
 * Création d'une fiche client.
 *
 * Ouverte depuis l'annuaire, mais aussi depuis un formulaire de commande ou de
 * vente quand le nom saisi ne correspond à personne : d'où le préremplissage et
 * le rappel `onCree`, qui laisse l'appelant rattacher la saisie en cours au
 * client tout juste créé plutôt que de le renvoyer sur une autre page.
 */
export function NouveauClientDialog({
  ouvert,
  onFermer,
  nomInitial = '',
  telInitial = '',
  onCree,
}: {
  ouvert: boolean;
  onFermer: () => void;
  nomInitial?: string;
  telInitial?: string;
  /** Appelé après création. Sans lui, le dialogue se contente de se fermer. */
  onCree?: (client: Client) => void;
}) {
  const [form, setForm] = useState<ClientInput>(VIDE);
  /** État d'ouverture : le préremplissage ne compte pas comme une saisie. */
  const [initial, setInitial] = useState<ClientInput>(VIDE);
  const [erreur, setErreur] = useState<string | null>(null);
  const creer = useCreerClient();
  const toast = useToast();

  useEffect(() => {
    if (!ouvert) return;
    // Ce qui a déjà été tapé dans le champ appelant ne doit pas être ressaisi.
    const depart: ClientInput = { ...VIDE, nom: nomInitial, tel: telInitial || null };
    setForm(depart);
    setInitial(depart);
    setErreur(null);
  }, [ouvert, nomInitial, telInitial]);

  /* Comparé au départ, pas au vide : sinon Échap réclamerait confirmation dès
   * l'ouverture, le nom étant déjà repris du champ appelant. */
  const modifie = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial],
  );

  function set<K extends keyof ClientInput>(cle: K, valeur: ClientInput[K]) {
    setForm((f) => ({ ...f, [cle]: valeur }));
    setErreur(null);
  }

  async function soumettre() {
    if (!form.nom.trim()) {
      setErreur('Le nom est obligatoire.');
      return;
    }
    try {
      const client = await creer.mutateAsync({
        nom: form.nom.trim(),
        tel: form.tel?.trim() || null,
        ice: form.ice?.trim() || null,
        note: form.note?.trim() || null,
      });
      toast.succes(`Fiche client « ${client.nom} » créée.`);
      onCree?.(client);
      onFermer();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "Le client n'a pas pu être créé.");
    }
  }

  return (
    <Dialog
      ouvert={ouvert}
      onFermer={onFermer}
      titre="Nouveau client"
      description="Une fiche client se crée aussi automatiquement à la première commande."
      taille="sm"
      modifie={modifie && !creer.isPending}
      pied={
        <>
          <Button variant="outline" onClick={onFermer} disabled={creer.isPending}>
            Annuler
          </Button>
          <Button variant="primary" onClick={() => void soumettre()} chargement={creer.isPending}>
            Créer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <InputField
          label="Nom & Prénom"
          value={form.nom}
          onChange={(e) => set('nom', e.target.value)}
          erreur={erreur ?? undefined}
          requis
        />
        <InputField
          label="Téléphone"
          value={form.tel ?? ''}
          onChange={(e) => set('tel', e.target.value)}
        />
        <InputField
          label="ICE"
          value={form.ice ?? ''}
          onChange={(e) => set('ice', e.target.value)}
          aide="Pour les clients professionnels facturés."
        />
        <TextareaField
          label="Note"
          value={form.note ?? ''}
          onChange={(e) => set('note', e.target.value)}
          rows={2}
        />
      </div>
    </Dialog>
  );
}
